import type { AppResource } from '@studio/shared';
import { useState, type ReactNode } from 'react';
import { api, ApiError } from '../shell/api.js';
import { Link } from '../shell/router.js';

/** An App that is still worked on: the only kind a list of Apps offers. */
export function isInWork(app: AppResource): boolean {
  return app.archivedAt === null;
}

function since(at: string): string {
  return new Date(at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * The archive: the Apps taken out of the lists, each one press from coming back. It
 * stays closed until asked for - it is where an App is looked for, not something read
 * every day - and it is not there at all while nothing is in it.
 */
export function ArchivedApps({
  apps,
  onRestored,
}: {
  apps: readonly AppResource[];
  onRestored: (app: AppResource) => void;
}): ReactNode {
  const [restoring, setRestoring] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  if (apps.length === 0) return null;

  const restore = (app: AppResource): void => {
    setRestoring(app.id);
    setFailure(null);
    void api
      .restoreApp(app.id)
      .then(onRestored)
      .catch((caught: unknown) => setFailure(caught instanceof ApiError ? caught.message : 'Не удалось вернуть App из архива.'))
      .finally(() => setRestoring(null));
  };

  return (
    <details className="app-archive">
      <summary>{`Архив · App: ${apps.length}`}</summary>
      <p className="note">
        App в архиве не показывается в Workspace и Inventory, и Scan&nbsp;all его не читает. Его Store Listings, Listing Snapshots и
        Experiments сохранены.
      </p>
      {failure === null ? null : (
        <p className="failure" role="alert">
          {failure}
        </p>
      )}
      <ul aria-label="В архиве">
        {apps.map((app) => (
          <li key={app.id}>
            <span className="archived-app">
              <Link to={`/apps/${app.id}`}>{app.displayName}</Link>
              <span className="archived-id">{app.packageName ?? 'не опубликовано в Google Play'}</span>
            </span>
            {app.archivedAt === null ? null : <span className="note">{`в архиве с ${since(app.archivedAt)}`}</span>}
            {/* The line it stands on names the App; what a reader hears names it too. */}
            <button
              type="button"
              className="secondary"
              disabled={restoring === app.id}
              aria-label={`Вернуть из архива: ${app.displayName}`}
              onClick={() => restore(app)}
            >
              Вернуть из архива
            </button>
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * The App's own page says whether it is in the archive and is where any App - one that
 * was never on Google Play included - is put there or taken back out.
 */
export function AppArchive({ app, onChanged }: { app: AppResource; onChanged: () => void }): ReactNode {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const archived = app.archivedAt !== null;

  const press = (): void => {
    setBusy(true);
    setFailure(null);
    void (archived ? api.restoreApp(app.id) : api.archiveApp(app.id))
      .then(onChanged)
      .catch((caught: unknown) =>
        setFailure(
          caught instanceof ApiError ? caught.message : archived ? 'Не удалось вернуть App из архива.' : 'Не удалось убрать App в архив.',
        ),
      )
      .finally(() => setBusy(false));
  };

  return (
    <div className={archived ? 'app-archive-state archived' : 'app-archive-state'}>
      {app.archivedAt === null ? null : (
        <p role="status">{`В архиве с ${since(app.archivedAt)}: не показывается в Workspace и Inventory, Scan\u00a0all его не читает.`}</p>
      )}
      <button type="button" className={archived ? 'secondary' : 'ghost'} disabled={busy} onClick={press}>
        {archived ? 'Вернуть из архива' : 'Убрать App в архив'}
      </button>
      {failure === null ? null : (
        <p className="failure" role="alert">
          {failure}
        </p>
      )}
    </div>
  );
}
